import { getSchema } from '@tiptap/core'
import type { Extensions } from '@tiptap/core'
import { Highlight } from '@tiptap/extension-highlight'
import { BlockMath, InlineMath } from '@tiptap/extension-mathematics'
import { Subscript } from '@tiptap/extension-subscript'
import { Superscript } from '@tiptap/extension-superscript'
import { TableKit } from '@tiptap/extension-table'
import { TaskItem } from '@tiptap/extension-task-item'
import { TaskList } from '@tiptap/extension-task-list'
import { TextAlign } from '@tiptap/extension-text-align'
import StarterKit from '@tiptap/starter-kit'
import type { Schema } from '@tiptap/pm/model'
import { YChangeAttribute, YChangeMark } from '../../../collab/ychange.js'
import { CommentMark } from '../../../comments/CommentMark.js'
import { Figure } from './figure.js'
import { Reference } from './reference.js'

/**
 * De knooptypen van een rich-text-veld, één keer, en gedeeld tussen de gemonteerde editor
 * (`Richtext.tsx`) en elke plek die een fragment buiten die editor om leest of schrijft (R-50).
 *
 * **Waarom dit een eigen bestand is en niet een tweede lijst in `Richtext.tsx`.** `y-tiptap`
 * gooit een fout bij een knooptype dat niet in het meegegeven `Schema` staat, en de afvangtak
 * verwijdert de knoop dan uit het levende `Y.Doc` in plaats van hem alleen niet te tonen —
 * dezelfde valkuil als het ychange-schema (`Richtext.tsx`, valkuil 2). Een tweede, met de hand
 * bijgehouden lijst zou vroeg of laat uiteenlopen; deze lijst is de enige.
 *
 * `Collaboration` en `CollaborationCaret` staan hier bewust niet bij: ze dragen geen eigen
 * knooptype bij aan het schema en hebben beide runtime-configuratie (een fragment, een provider)
 * die hier niet beschikbaar is.
 */
export const RICH_TEXT_EXTENSIONS: Extensions = [
  // undoRedo uit: naar het patroon van Richtext.tsx (valkuil 5), voor schema-doeleinden zonder
  // gevolgen, want een schema draagt geen historie. `heading.levels` beperkt tot 1-3, naar
  // CitatenPOC's eigen koppenkiezer ("tekst/kop1-3"): een formulierveld is geen document met
  // een eigen kopstructuur van zes niveaus diep.
  StarterKit.configure({ undoRedo: false, heading: { levels: [1, 2, 3] } }),
  // Niet de gebundelde `Mathematics`-extensie: die geeft `InlineMath` en `BlockMath` altijd
  // dezelfde `katexOptions`, en juist `displayMode` moet per soort verschillen — blok groter
  // en gecentreerd op een eigen regel, inline meelopend met de tekst. Zelfde `throwOnError:
  // false` als CitatenPOC: KaTeX toont dan een foutindicatie op de plek zelf in plaats van de
  // node view die zonder dat op ruwe LaTeX-tekst terugvalt.
  BlockMath.configure({ katexOptions: { displayMode: true, throwOnError: false } }),
  InlineMath.configure({ katexOptions: { throwOnError: false } }),
  // `resizable: false`: een sleepbare kolomrand is een muis-only interactie, en R-158/T14
  // eisen dat het formulier op een tablet volledig werkt. Terug aan te zetten zodra er een
  // aanraakvriendelijke manier is om een kolom breder of smaller te maken.
  TableKit.configure({ table: { resizable: false } }),
  // Knoppariteit met CitatenPOC's balk, batch 2 (eisenboek 12.9). Taaklijst draagt een eigen
  // knooptype (`taskList`/`taskItem`, zie `YCHANGE_NODES` in ychange.ts); subscript, superscript
  // en highlight zijn marks zoals `underline`/`strike` uit StarterKit hierboven en hebben geen
  // eigen regel in dat schema nodig. `Highlight.configure({ multicolor: false })`: één
  // toggle-markering, geen vrije kleurkiezer — CitatenPOC's eigen keuze, en de enige verdedigbare
  // hier: `docs/bron/designsysteem/nwo.tokens.json` is de enige bron van waarheid voor kleur, en
  // een vrij `<input type="color">` zou een applicant-gekozen hexwaarde in de revisie zetten.
  TaskList,
  // `nested: true`: naar CitatenPOC's eigen configuratie, een taak kan een sublijst dragen.
  TaskItem.configure({ nested: true }),
  Subscript,
  Superscript,
  Highlight.configure({ multicolor: false }),
  // Alleen op paragraaf en kop: een tabelcel of lijstitem uitlijnen voegt niets toe en de
  // extensie voegt dan alleen maar een ongebruikt attribuut aan die knopen toe.
  TextAlign.configure({ types: ['paragraph', 'heading'] }),
  Figure,
  Reference,
  YChangeMark,
  YChangeAttribute,
  CommentMark,
]

let cached: Schema | undefined

/**
 * Het ProseMirror-schema van een rich-text-veld, bruikbaar zonder gemonteerde editor.
 *
 * Eén keer opgebouwd en daarna hergebruikt. Gemeten: `getSchema` kost ongeveer 0,16 ms per
 * aanroep, en `answerOf` roept hem per richtext-veld aan — bij `useStand` dus per veld, bij
 * elke Yjs-`update` (dat leek eerst verwaarloosbaar, maar bleek bij het meten ongeveer een derde
 * van de tijd van die hele berekening; `motivering-eerst-meten`). Een `Schema` is onveranderlijk
 * na het bouwen, dus hergebruik tussen aanroepen heeft geen synchronisatieprobleem: hetzelfde
 * patroon als een gemonteerde `Editor` zijn `Schema` voor zijn hele levensduur vasthoudt.
 */
export function richTextSchema(): Schema {
  return (cached ??= getSchema(RICH_TEXT_EXTENSIONS))
}
